import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  Calendar, 
  MapPin, 
  Search, 
  Send, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Filter,
  X,
  Calendar as CalendarIcon,
  MoreVertical,
  CheckCircle,
  XCircle
} from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTenders } from "@/hooks/api/use-tenders";
import { 
  useAdminTenders, 
  useCreateTender, 
  useUpdateTender, 
  useDeleteTender,
  useAdminSpecializations,
  useAdminClients
} from "@/hooks/api/use-admin";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { CreateTenderRequest, Tender } from "@/api/types";
import ReadOnlyWrapper from "@/components/ReadOnlyWrapper";

const ClientTenders = () => {
  const { t, isRTL, language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.type === 'admin';
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'closed' | 'pending'>('all');
  const [selectedCategory, setSelectedCategory] = useState("all");
  const observerTarget = useRef<HTMLDivElement>(null);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTender, setEditingTender] = useState<Tender | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateTenderRequest>({
    title: { en: '', ar: '' },
    description: { en: '', ar: '' },
    requirements: { en: '', ar: '' },
    budget_min: 0,
    budget_max: 0,
    deadline: '',
    status: 'active',
    visibility_type: 'all',
    specialization_ids: [],
    client_ids: [],
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch specializations and clients for admin
  const { data: specializationsData } = useAdminSpecializations({ enabled: isAdmin && !!user });
  const { data: clientsData } = useAdminClients({ limit: 100 }, { enabled: isAdmin && !!user });
  
  const specializations = specializationsData?.data || [];
  const clients = clientsData?.pages?.flatMap((page) => page?.data?.data || []) || [];

  // Filters
  const filters = {
    page: 1,
    per_page: 15,
    search: debouncedSearchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    specialization_id: selectedCategory !== "all" ? parseInt(selectedCategory) : undefined,
  };

  // Fetch tenders
  const { data: clientData, isLoading: clientLoading, error: clientError } = useTenders(
    filters, 
    { enabled: !isAdmin && !!user }
  );
  const { data: adminData, isLoading: adminLoading, error: adminError, fetchNextPage, hasNextPage, isFetchingNextPage } = useAdminTenders(
    { ...filters, page: undefined }, // Remove page from filters as useInfiniteQuery handles it
    { enabled: isAdmin && !!user }
  );
  
  const isLoading = isAdmin ? adminLoading : clientLoading;
  const error = isAdmin ? adminError : clientError;

  // Flatten tenders (for admin with infinite scroll)
  const tenders = isAdmin 
    ? (adminData?.pages?.flatMap((page) => page?.data?.data || []) || [])
    : (Array.isArray(clientData?.data) ? clientData.data : []);

  // Mutations
  const createTender = useCreateTender();
  const updateTender = useUpdateTender();
  const deleteTender = useDeleteTender();

  // Infinite scroll for admin
  useEffect(() => {
    if (!isAdmin) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isAdmin]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      closed: {
        label: t('tenders.closed') || 'مغلقة',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
      },
      active: {
        label: language === 'en' ? 'Active' : 'نشط',
        className: '',
      },
      pending: {
        label: t('tenders.pending') || 'قيد المراجعة',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const handleOpenDialog = (tender?: Tender) => {
    if (tender) {
      setEditingTender(tender);
      const title = typeof tender.title === 'string' 
        ? { en: tender.title, ar: tender.title }
        : tender.title;
      const description = typeof tender.description === 'string'
        ? { en: tender.description, ar: tender.description }
        : tender.description || { en: '', ar: '' };
      const requirements = typeof tender.requirements === 'string'
        ? { en: tender.requirements, ar: tender.requirements }
        : tender.requirements || { en: '', ar: '' };
      
      setFormData({
        title,
        description,
        requirements,
        budget_min: tender.budget_min || 0,
        budget_max: tender.budget_max || 0,
        deadline: tender.deadline || '',
        status: tender.status || 'active',
        visibility_type: tender.visibility_type || 'all',
        specialization_ids: tender.specialization_ids || [],
        client_ids: tender.client_ids || [],
      });
    } else {
      setEditingTender(null);
      setFormData({
        title: { en: '', ar: '' },
        description: { en: '', ar: '' },
        requirements: { en: '', ar: '' },
        budget_min: 0,
        budget_max: 0,
        deadline: '',
        status: 'active',
        visibility_type: 'all',
        specialization_ids: [],
        client_ids: [],
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTender(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.en || !formData.title.ar) {
      toast({
        title: t('common.error') || 'خطأ',
        description: t('tenders.titleRequired') || 'يجب إدخال العنوان باللغتين',
        variant: 'destructive',
      });
      return;
    }

    if (formData.budget_min > formData.budget_max) {
      toast({
        title: t('common.error') || 'خطأ',
        description: t('tenders.budgetError') || 'الحد الأدنى يجب أن يكون أقل من الحد الأقصى',
        variant: 'destructive',
      });
      return;
    }

    if (formData.visibility_type === 'specializations' && (!formData.specialization_ids || formData.specialization_ids.length === 0)) {
      toast({
        title: t('common.error') || 'خطأ',
        description: t('tenders.specializationsRequired') || 'يجب اختيار اختصاص واحد على الأقل',
        variant: 'destructive',
      });
      return;
    }

    if (formData.visibility_type === 'specific_clients' && (!formData.client_ids || formData.client_ids.length === 0)) {
      toast({
        title: t('common.error') || 'خطأ',
        description: t('tenders.clientsRequired') || 'يجب اختيار عميل واحد على الأقل',
        variant: 'destructive',
      });
      return;
    }

    const submitData: CreateTenderRequest = {
      ...formData,
      specialization_ids: formData.visibility_type === 'specializations' ? formData.specialization_ids : undefined,
      client_ids: formData.visibility_type === 'specific_clients' ? formData.client_ids : undefined,
    };

    if (editingTender) {
      updateTender.mutate(
        { id: editingTender.id, data: submitData },
        {
          onSuccess: () => {
            handleCloseDialog();
          },
        }
      );
    } else {
      createTender.mutate(submitData, {
        onSuccess: () => {
          handleCloseDialog();
        },
      });
    }
  };

  const handleDelete = (tenderId: number) => {
    if (confirm(t('tenders.confirmDelete') || 'هل أنت متأكد من حذف هذه المناقصة؟')) {
      deleteTender.mutate(tenderId);
    }
  };

  const handleUpdateStatus = (tenderId: number, status: 'active' | 'closed') => {
    updateTender.mutate(
      { id: tenderId, data: { status } },
      {
        onSuccess: () => {
          toast({
            title: t('tenders.statusUpdated') || 'تم تحديث الحالة',
            description: t('tenders.statusUpdatedDesc') || 'تم تحديث حالة المناقصة بنجاح',
          });
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isAdmin ? (t('tenders.manage') || 'إدارة المناقصات') : (t('tenders.available') || 'المناقصات المتاحة')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? (t('tenders.manageDesc') || 'إنشاء وإدارة المناقصات') : (t('tenders.browse') || 'تصفح المناقصات المتاحة')}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => handleOpenDialog()} className="gap-2">
            <Plus size={18} />
            {t('tenders.create') || 'إنشاء مناقصة'}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-card border border-border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} size={18} />
              <Input
                placeholder={t('tenders.search') || 'البحث...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={isRTL ? "pr-10" : "pl-10"}
              />
            </div>
            {isAdmin && (
              <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter size={16} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                  <SelectValue placeholder={t('tenders.filterStatus') || 'فلترة حسب الحالة'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('tenders.allStatuses') || 'جميع الحالات'}</SelectItem>
                  <SelectItem value="active">{t('tenders.open') || 'مفتوحة'}</SelectItem>
                  <SelectItem value="closed">{t('tenders.closed') || 'مغلقة'}</SelectItem>
                  <SelectItem value="pending">{t('tenders.pending') || 'قيد المراجعة'}</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder={t('tenders.specialization') || 'الاختصاص'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('tenders.allSpecializations') || 'جميع الاختصاصات'}</SelectItem>
                {specializations.map((spec: any) => {
                  const specName = typeof spec.name === 'string' 
                    ? spec.name 
                    : spec.name?.[language] || spec.name?.en || '';
                  return (
                    <SelectItem key={spec.id} value={spec.id.toString()}>
                      {specName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tenders List */}
      <div className="space-y-4">
        {isLoading ? (
          [1, 2, 3].map((i) => (
            <Card key={i} className="bg-card border border-border">
              <CardContent className="p-6">
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ))
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('common.error') || 'حدث خطأ أثناء تحميل المناقصات'}</p>
          </div>
        ) : tenders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('tenders.noTenders') || 'لا توجد مناقصات متاحة'}</p>
          </div>
        ) : (
          <>
            {tenders.map((tender: any) => {
              const title = typeof tender.title === 'string' 
                ? tender.title 
                : tender.title?.[language] || tender.title?.en || '';
              const description = tender.description 
                ? (typeof tender.description === 'string' 
                    ? tender.description 
                    : tender.description?.[language] || tender.description?.en || '')
                : '';
              const requirements = tender.requirements
                ? (typeof tender.requirements === 'string'
                    ? tender.requirements
                    : tender.requirements?.[language] || tender.requirements?.en || '')
                : '';
              
              return (
                <Card key={tender.id} className="bg-card border border-border overflow-hidden hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="flex flex-col lg:flex-row">
                      {/* Tender Info */}
                      <div className="flex-1 p-6">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          {getStatusBadge(tender.status)}
                          {tender.visibility_type === 'specializations' && (
                            <Badge variant="outline">{t('tenders.bySpecializations') || 'حسب الاختصاصات'}</Badge>
                          )}
                          {tender.visibility_type === 'specific_clients' && (
                            <Badge variant="outline">{t('tenders.specificClients') || 'عملاء محددون'}</Badge>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-foreground mb-2">
                          {title}
                        </h3>
                        {description && (
                          <p className="text-muted-foreground text-sm mb-2 line-clamp-2">
                            {description}
                          </p>
                        )}
                        {requirements && (
                          <p className="text-muted-foreground text-xs mb-3">
                            <strong>{t('tenders.requirements') || 'المتطلبات'}:</strong> {requirements}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          {tender.deadline && (
                            <span className="flex items-center gap-1">
                              <Calendar size={16} />
                              {(() => {
                                try {
                                  const date = new Date(tender.deadline);
                                  if (isNaN(date.getTime())) return tender.deadline;
                                  return format(date, "PPP", { locale: isRTL ? ar : enUS });
                                } catch {
                                  return tender.deadline;
                                }
                              })()}
                            </span>
                          )}
                          {(tender.budget_min || tender.budget_max) && (
                            <span className="text-primary font-bold">
                              {isRTL ? (t('tenders.sar') || 'ريال') + ' ' : ''}
                              {tender.budget_min && tender.budget_max 
                                ? `${tender.budget_min.toLocaleString()} - ${tender.budget_max.toLocaleString()}`
                                : tender.budget_min 
                                  ? `${tender.budget_min.toLocaleString()}+`
                                  : `${tender.budget_max?.toLocaleString()}`}
                              {isRTL ? '' : ' ' + (t('tenders.sar') || 'SAR')}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center justify-center gap-2 p-6 lg:border-l border-t lg:border-t-0 border-border bg-muted/30">
                        {isAdmin ? (
                          <div className="flex flex-col sm:flex-row gap-2 items-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenDialog(tender)}
                              className="gap-2"
                            >
                              <Edit size={16} />
                              {t('tenders.edit') || 'تعديل'}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-2">
                                  <MoreVertical size={16} />
                                  {t('tenders.changeStatus') || 'تغيير الحالة'}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align={isRTL ? "start" : "end"}>
                                <DropdownMenuItem
                                  onClick={() => handleUpdateStatus(tender.id, 'active')}
                                  disabled={tender.status === 'active' || updateTender.isPending}
                                >
                                  <CheckCircle className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                                  {t('tenders.open') || 'مفتوحة'}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleUpdateStatus(tender.id, 'closed')}
                                  disabled={tender.status === 'closed' || updateTender.isPending}
                                >
                                  <XCircle className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                                  {t('tenders.closed') || 'مغلقة'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(tender.id)}
                              disabled={deleteTender.isPending}
                              className="gap-2"
                            >
                              <Trash2 size={16} />
                              {t('tenders.delete') || 'حذف'}
                            </Button>
                          </div>
                        ) : (
                          <ReadOnlyWrapper>
                            <Button asChild className="gap-2">
                              <Link to={`/dashboard/tenders/${tender.id}`}>
                                <Send size={16} />
                                {t('tenders.submitOffer') || 'تقديم عرض'}
                              </Link>
                            </Button>
                          </ReadOnlyWrapper>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* Infinite Scroll Trigger for Admin */}
            {isAdmin && hasNextPage && (
              <div ref={observerTarget} className="py-4">
                {isFetchingNextPage && (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-32 w-full rounded-xl" />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      {isAdmin && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTender 
                  ? (t('tenders.editTender') || 'تعديل المناقصة')
                  : (t('tenders.createTender') || 'إنشاء مناقصة جديدة')
                }
              </DialogTitle>
              <DialogDescription>
                {editingTender
                  ? (t('tenders.editTenderDesc') || 'قم بتعديل بيانات المناقصة')
                  : (t('tenders.createTenderDesc') || 'قم بإنشاء مناقصة جديدة')
                }
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 pt-4">
              {/* Title */}
              <div className="space-y-4">
                <div>
                  <Label>{t('tenders.titleEn') || 'العنوان (إنجليزي)'}</Label>
                  <Input
                    value={formData.title.en}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                    placeholder={t('tenders.titleEnPlaceholder') || 'Enter title in English'}
                    required
                  />
                </div>
                <div>
                  <Label>{t('tenders.titleAr') || 'العنوان (عربي)'}</Label>
                  <Input
                    value={formData.title.ar}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                    placeholder={t('tenders.titleArPlaceholder') || 'أدخل العنوان بالعربية'}
                    required
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <div>
                  <Label>{t('tenders.descriptionEn') || 'الوصف (إنجليزي)'}</Label>
                  <Textarea
                    value={formData.description.en}
                    onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                    placeholder={t('tenders.descriptionEnPlaceholder') || 'Enter description in English'}
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <Label>{t('tenders.descriptionAr') || 'الوصف (عربي)'}</Label>
                  <Textarea
                    value={formData.description.ar}
                    onChange={(e) => setFormData({ ...formData, description: { ...formData.description, ar: e.target.value } })}
                    placeholder={t('tenders.descriptionArPlaceholder') || 'أدخل الوصف بالعربية'}
                    rows={4}
                    required
                  />
                </div>
              </div>

              {/* Requirements */}
              <div className="space-y-4">
                <div>
                  <Label>{t('tenders.requirementsEn') || 'المتطلبات (إنجليزي)'}</Label>
                  <Textarea
                    value={formData.requirements?.en || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      requirements: { 
                        en: e.target.value, 
                        ar: formData.requirements?.ar || '' 
                      } 
                    })}
                    placeholder={t('tenders.requirementsEnPlaceholder') || 'Enter requirements in English'}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>{t('tenders.requirementsAr') || 'المتطلبات (عربي)'}</Label>
                  <Textarea
                    value={formData.requirements?.ar || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      requirements: { 
                        en: formData.requirements?.en || '', 
                        ar: e.target.value 
                      } 
                    })}
                    placeholder={t('tenders.requirementsArPlaceholder') || 'أدخل المتطلبات بالعربية'}
                    rows={3}
                  />
                </div>
              </div>

              {/* Budget */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('tenders.budgetMin') || 'الحد الأدنى للميزانية'}</Label>
                  <Input
                    type="number"
                    value={formData.budget_min}
                    onChange={(e) => setFormData({ ...formData, budget_min: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <Label>{t('tenders.budgetMax') || 'الحد الأقصى للميزانية'}</Label>
                  <Input
                    type="number"
                    value={formData.budget_max}
                    onChange={(e) => setFormData({ ...formData, budget_max: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>
              </div>

              {/* Deadline */}
              <div>
                <Label>{t('tenders.deadline') || 'آخر موعد للتقديم'}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.deadline && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                      {formData.deadline 
                        ? format(new Date(formData.deadline), "PPP", { locale: isRTL ? ar : enUS })
                        : (t('tenders.selectDeadline') || 'اختر التاريخ')
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.deadline ? new Date(formData.deadline) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          setFormData({ ...formData, deadline: format(date, "yyyy-MM-dd") });
                        }
                      }}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Status */}
              <div>
                <Label>{t('tenders.status') || 'الحالة'}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: any) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('tenders.open') || 'مفتوحة'}</SelectItem>
                    <SelectItem value="closed">{t('tenders.closed') || 'مغلقة'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Visibility Type */}
              <div>
                <Label>{t('tenders.visibilityType') || 'نوع الرؤية'}</Label>
                <Select
                  value={formData.visibility_type}
                  onValueChange={(value: any) => {
                    setFormData({ 
                      ...formData, 
                      visibility_type: value,
                      specialization_ids: value !== 'specializations' ? [] : formData.specialization_ids,
                      client_ids: value !== 'specific_clients' ? [] : formData.client_ids,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('tenders.allClients') || 'جميع العملاء'}</SelectItem>
                    <SelectItem value="specializations">{t('tenders.bySpecializations') || 'حسب الاختصاصات'}</SelectItem>
                    <SelectItem value="specific_clients">{t('tenders.specificClients') || 'عملاء محددون'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Specializations */}
              {formData.visibility_type === 'specializations' && (
                <div>
                  <Label>{t('tenders.selectSpecializations') || 'اختر الاختصاصات'}</Label>
                  <div className="border border-border rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                    {specializations.map((spec: any) => {
                      const specName = typeof spec.name === 'string' 
                        ? spec.name 
                        : spec.name?.[language] || spec.name?.en || '';
                      return (
                        <div key={spec.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`spec-${spec.id}`}
                            checked={formData.specialization_ids?.includes(spec.id)}
                            onCheckedChange={(checked) => {
                              const currentIds = formData.specialization_ids || [];
                              if (checked) {
                                setFormData({ ...formData, specialization_ids: [...currentIds, spec.id] });
                              } else {
                                setFormData({ ...formData, specialization_ids: currentIds.filter(id => id !== spec.id) });
                              }
                            }}
                          />
                          <Label htmlFor={`spec-${spec.id}`} className="cursor-pointer">
                            {specName}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Specific Clients */}
              {formData.visibility_type === 'specific_clients' && (
                <div>
                  <Label>{t('tenders.selectClients') || 'اختر العملاء'}</Label>
                  <div className="border border-border rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                    {clients.filter((client: any) => client.status === 'approved').map((client: any) => {
                      const clientId = client.client_id || client.id;
                      return (
                        <div key={client.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`client-${clientId}`}
                            checked={formData.client_ids?.includes(clientId)}
                            onCheckedChange={(checked) => {
                              const currentIds = formData.client_ids || [];
                              if (checked) {
                                setFormData({ ...formData, client_ids: [...currentIds, clientId] });
                              } else {
                                setFormData({ ...formData, client_ids: currentIds.filter(id => id !== clientId) });
                              }
                            }}
                          />
                          <Label htmlFor={`client-${clientId}`} className="cursor-pointer">
                            {client.name} {client.company_name && `- ${client.company_name}`}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-border">
                <Button
                  type="submit"
                  disabled={createTender.isPending || updateTender.isPending}
                  className="flex-1 gap-2"
                >
                  {editingTender 
                    ? (t('tenders.update') || 'تحديث')
                    : (t('tenders.create') || 'إنشاء')
                  }
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  className="flex-1"
                >
                  {t('common.cancel') || 'إلغاء'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ClientTenders;
